export type OverviewFilterKind =
  | "all"
  | "language"
  | "course"
  | "participant"
  | "department";

export interface FilterOption {
  value: string;
  label: string;
}

type QueryParams = Record<string, string | number>;

const COURSE_SCOPED_FILTER_KINDS: OverviewFilterKind[] = [
  "all",
  "participant",
  "department",
];

const INVALID_COURSE_SCOPED_FILTER_KINDS: OverviewFilterKind[] = [
  "language",
  "course",
];

export const getStatusOverviewFilterKindOptions = (
  isCourseScoped: boolean,
  t: (key: string) => string
) => {
  if (isCourseScoped) {
    return [
      { value: "all", label: t("all") },
      { value: "participant", label: t("specific_participant") },
      { value: "department", label: t("department") },
    ];
  }

  return [
    { value: "all", label: t("all_courses") },
    { value: "language", label: t("language") },
    { value: "course", label: t("specific_course") },
    { value: "participant", label: t("specific_participant") },
    { value: "department", label: t("department") },
  ];
};

export const isInvalidCourseScopedFilterKind = (
  filterKind: OverviewFilterKind
): boolean => INVALID_COURSE_SCOPED_FILTER_KINDS.includes(filterKind);

export const getStatusOverviewFilterParams = ({
  filterKind,
  selectedLanguage,
  selectedCourse,
  selectedParticipant,
  selectedDepartment,
}: {
  filterKind: OverviewFilterKind;
  selectedLanguage: FilterOption | null;
  selectedCourse: FilterOption | null;
  selectedParticipant: FilterOption | null;
  selectedDepartment: FilterOption | null;
}): QueryParams | null => {
  switch (filterKind) {
    case "all":
      return {};
    case "language":
      return selectedLanguage ? { language: selectedLanguage.value } : null;
    case "course":
      return selectedCourse ? { course__id: selectedCourse.value } : null;
    case "participant":
      return selectedParticipant
        ? { participant_id: selectedParticipant.value }
        : null;
    case "department":
      return selectedDepartment
        ? { department: selectedDepartment.value }
        : null;
    default:
      return {};
  }
};

export const getDashboardStatusOverviewFilterParams = ({
  filterKind,
  selectedFilter,
}: {
  filterKind: OverviewFilterKind;
  selectedFilter: FilterOption | null;
}): QueryParams | null => {
  if (filterKind === "all") {
    return {};
  }

  if (!selectedFilter) {
    return null;
  }

  return { [`filter[${filterKind}]`]: selectedFilter.value };
};

export const getParticipantFilterOptionFromSubjectSearch = (
  subjectSearch: any
): FilterOption | null => {
  if (
    !subjectSearch ||
    typeof subjectSearch !== "object" ||
    Object.keys(subjectSearch).length === 0 ||
    !subjectSearch.value ||
    !subjectSearch.label ||
    subjectSearch.is_company === true ||
    "companyId" in subjectSearch
  ) {
    return null;
  }

  return {
    value: String(subjectSearch.value),
    label: String(subjectSearch.label),
  };
};

export const getStatusOverviewBaseQueryParams = ({
  courseId,
  participantId,
  dateFilterParams,
  filterParams,
  status,
}: {
  courseId?: string;
  participantId?: string | number;
  dateFilterParams: Record<string, string>;
  filterParams: QueryParams;
  status: string;
}): QueryParams => {
  const requestParams: QueryParams = {
    ...(participantId ? { participant_id: participantId } : {}),
    ...dateFilterParams,
    status,
    ...filterParams,
  };

  if (courseId) {
    requestParams.course_id = courseId;
  }

  return requestParams;
};

export const getCourseScopedFilterKinds = (): OverviewFilterKind[] =>
  COURSE_SCOPED_FILTER_KINDS;

export const shouldShowAdminCompanySubjectSearch = ({
  mode,
  filterKind,
  companySubjectId,
}: {
  mode: string;
  filterKind: OverviewFilterKind;
  companySubjectId?: string;
}): boolean =>
  mode === "administrator" && filterKind === "department" && !companySubjectId;

export const resolveDepartmentCompanySubjectId = ({
  mode,
  companySubjectId,
  selectedChildCompanySubjectId,
  managerCompanySubjectId,
  subjectSearchCompanySubjectId,
}: {
  mode: string;
  companySubjectId?: string;
  selectedChildCompanySubjectId?: string;
  managerCompanySubjectId?: string;
  subjectSearchCompanySubjectId?: string;
}): string | undefined => {
  if (companySubjectId) {
    return companySubjectId;
  }

  if (mode === "manager") {
    return (
      selectedChildCompanySubjectId || managerCompanySubjectId || undefined
    );
  }

  if (mode === "administrator") {
    return subjectSearchCompanySubjectId;
  }

  return undefined;
};
